// What the activity timeline shows for each entry (spec 6.11): a sentence per kind from the entry's targets and
// details, the actor in words, and the settings changes as lines. Unknown kinds are shown by name. Nothing here
// can render a secret value: entries never hold one, and only the members listed here are read.
import type { ActivityEntry, ActivityFilter, ActivityKind, JsonObject, JsonValue } from '../api/types';
import {
  NETWORK_ACTIVITY_KINDS,
  NETWORK_COMMITMENT_ACTIVITY_KINDS,
  NETWORK_ORIGIN_ACTIVITY_KINDS,
  NETWORK_REQUEST_ACTIVITY_KINDS,
} from '../api/types';
import { formatDay } from '../lib/format';
import { strings } from '../strings';

const a = strings.activity;
const t = a.sentence;
const na = strings.networks.runs.activity;
const ra = strings.networks.requests.activityParts;

/** `network` (feature spec 17): offered in the Kind select only while networking is on. */
export type ActivityGroup = 'settings' | 'runs' | 'secrets' | 'apps' | 'network';

export const ACTIVITY_GROUPS: Record<ActivityGroup, readonly ActivityKind[]> = {
  settings: ['settings-changed', 'deployment-created', 'deployment-deleted'],
  runs: ['run-created', 'run-completed'],
  secrets: [
    'secret-set',
    'secret-generated',
    'secret-accepted',
    'secret-ignored',
    'secret-recovered',
    'secret-changed-outside',
    'tags-rewritten',
    'secret-revealed',
    'secret-rotated',
    'vault-secrets-deleted',
    'vault-secrets-restored',
  ],
  apps: ['references-refreshed', 'app-restarted'],
  network: [
    ...NETWORK_ACTIVITY_KINDS,
    ...NETWORK_REQUEST_ACTIVITY_KINDS,
    ...NETWORK_ORIGIN_ACTIVITY_KINDS,
    ...NETWORK_COMMITMENT_ACTIVITY_KINDS,
  ] as unknown as readonly ActivityKind[],
};

export function activityGroup(kind: string): ActivityGroup | null {
  for (const [group, kinds] of Object.entries(ACTIVITY_GROUPS) as [ActivityGroup, readonly string[]][])
    if (kinds.includes(kind)) return group;
  return null;
}

/** The secret or resource an entry is about: shown as a button where it still exists (spec 6.11.3). */
export interface ActivityTarget {
  kind: 'secret' | 'resource';
  id: string;
}

/** Where the timeline can show its targets (spec 6.11.3): the ones that still exist, and how to show one. */
export interface ActivityTargets {
  secrets: ReadonlySet<string>;
  resources: ReadonlySet<string>;
  onShow: (target: ActivityTarget) => void;
}

/** A part of an entry's sentence: words (a link with `to`), or a target shown in monospace. */
export type ActivityPart = { text: string; to?: string } | { code: string; target?: ActivityTarget };

export interface ActivityChangeLine {
  label: string;
  before: string;
  after: string;
}

export interface ActivityDescription {
  group: ActivityGroup | null;
  parts: ActivityPart[];
  /** `settings-changed`: one line per changed leaf (at most `maxChanges`). */
  changes: ActivityChangeLine[];
  /** Changes left out. */
  more: number;
  runId: string | null;
}

/** The actor in words: "Nebisys" for the system, "Nebisys (rollback)" for a rollback run. */
export function actorLabel(entry: Pick<ActivityEntry, 'actorName'>): string {
  if (entry.actorName === 'system') return a.actor.system;
  if (entry.actorName === 'system:rollback') return a.actor.rollback;
  if (entry.actorName === 'system:network') return na.actor;
  return entry.actorName;
}

const unescape = (segment: string) => segment.replace(/~1/g, '/').replace(/~0/g, '~');

/** A changed path in words: `/settings/parameters/environment` → "parameter environment". */
export function pathLabel(path: string): string {
  const segments = path.split('/').slice(1).map(unescape);
  if (segments.length === 1 && segments[0] === 'name') return a.paths.name;
  if (segments[0] === 'settings') {
    if (segments[1] === 'parameters' && segments.length === 3) return a.paths.parameter(segments[2]);
    return segments.slice(1).join('.');
  }
  return segments.join('.') || path;
}

/** A JSON value as text: strings without quotes, "–" for null. */
export function jsonText(value: JsonValue | undefined): string {
  if (value === null || value === undefined) return strings.common.dash;
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

const text = (value: string): ActivityPart => ({ text: value });
const code = (value: string): ActivityPart => ({ code: value });
const str = (details: JsonObject, key: string): string | null =>
  typeof details[key] === 'string' ? (details[key] as string) : null;
const shortVersion = (id: string | null) => (id ? id.slice(0, 8) : null);

/** `details.changes` as lines ("parameter environment: dev → prod"). */
function changeLines(details: JsonObject): ActivityChangeLine[] {
  const list = Array.isArray(details.changes) ? details.changes : [];
  return list
    .filter((c): c is JsonObject => !!c && typeof c === 'object' && !Array.isArray(c))
    .map((c) => ({
      label: pathLabel(typeof c.path === 'string' ? c.path : ''),
      before: jsonText(c.before),
      after: jsonText(c.after),
    }));
}

function runKindName(details: JsonObject): string {
  return (str(details, 'kind') ?? 'run').toLowerCase();
}

/** "0199e4c2…a7": the first 8 and last 2 characters of an activity entry ID. */
export function entryReference(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-2)}` : id;
}

/**
 * Leaves out reveal request entries whose outcome entry is loaded too (spec 6.11): the outcome entry says the
 * same, and it is newer, so it is always loaded first. A request entry without one stays ("outcome unknown").
 */
export function foldRevealRequests<T extends Pick<ActivityEntry, 'id' | 'kind' | 'details'>>(
  entries: readonly T[],
): T[] {
  const answered = new Set<string>();
  for (const entry of entries)
    if (entry.kind === 'secret-revealed' && typeof entry.details?.requestId === 'string')
      answered.add(entry.details.requestId);
  return entries.filter((entry) => !(entry.kind === 'secret-revealed' && answered.has(entry.id)));
}

/** Where a timeline shows an entry: a network's timeline links the deployments that still exist (feature spec 17). */
export interface ActivityContext {
  network?: { deployments: ReadonlySet<string> };
}

export function describeActivity(
  entry: ActivityEntry,
  maxChanges = 10,
  context: ActivityContext = {},
): ActivityDescription {
  const details: JsonObject = entry.details && typeof entry.details === 'object' ? entry.details : {};
  const parts: ActivityPart[] = [];
  let changes: ActivityChangeLine[] = [];
  let more = 0;
  const secret: ActivityPart | null = entry.secretName
    ? { code: entry.secretName, target: { kind: 'secret', id: entry.secretName } }
    : null;
  const resource: ActivityPart | null = entry.resourceId
    ? { code: entry.resourceId, target: { kind: 'resource', id: entry.resourceId } }
    : null;
  const networkName = entry.networkName ?? str(details, 'networkName') ?? strings.networks.nav.kind;
  const deploymentPart = (suffix = ''): ActivityPart => {
    const name = `${entry.deploymentName || strings.networks.runLabels.aDeployment}${suffix}`;
    return context.network && entry.deploymentId && context.network.deployments.has(entry.deploymentId)
      ? { text: name, to: `/deployments/${entry.deploymentId}` }
      : text(name);
  };
  // A network run on a deployment's timeline (its connect and detach runs): "… of Production".
  const ofNetwork = !context.network && entry.networkName ? [text(na.of), text(entry.networkName)] : [];
  switch (entry.kind) {
    case 'deployment-created':
      parts.push(text(t.deploymentCreated), code(str(details, 'name') ?? entry.deploymentName));
      break;
    case 'deployment-deleted':
      parts.push(text(t.deploymentDeleted), code(str(details, 'name') ?? entry.deploymentName));
      break;
    case 'settings-changed': {
      parts.push(text(t.settingsChanged));
      const lines = changeLines(details);
      changes = lines.slice(0, maxChanges);
      more = lines.length - changes.length;
      break;
    }
    case 'run-created': {
      parts.push(text(t.runCreated(runKindName(details))), ...ofNetwork);
      if (str(details, 'scope') === 'vault') parts.push(text(`(${t.vaultOnly})`));
      if (typeof details.blueprintVersion === 'number')
        parts.push(text(t.ofVersion(details.blueprintVersion)));
      if (str(details, 'triggeredBy') === 'rollback') parts.push(text(t.rollingBack));
      break;
    }
    case 'run-completed': {
      const kind = runKindName(details);
      const status = str(details, 'status') ?? entry.outcome;
      // "apply run of Production failed" for a network run on a deployment's timeline.
      const withNetwork = (sentence: string) =>
        ofNetwork.length ? sentence.replace(/ run( |$)/, ` run ${na.of} ${entry.networkName}$1`) : sentence;
      if (status === 'failed') {
        parts.push(text(withNetwork(t.runFailed(kind))));
        const errorCode = str(details, 'errorCode');
        if (errorCode) parts.push(code(errorCode));
      } else if (status === 'canceled') parts.push(text(withNetwork(t.runCanceled(kind))));
      else parts.push(text(withNetwork(t.runCompleted(kind))));
      const createdBy = str(details, 'createdBy');
      if (createdBy) parts.push(text(`· ${t.startedBy(createdBy)}`));
      break;
    }
    case 'secret-set':
      parts.push(text(t.secretSet));
      if (secret) parts.push(secret);
      if (details.restart === true) parts.push(text(`· ${t.restarting}`));
      if (entry.versionId) parts.push(text('·'), code(shortVersion(entry.versionId)!));
      break;
    case 'secret-generated':
      parts.push(text(t.secretGenerated));
      if (secret) parts.push(secret);
      break;
    case 'secret-accepted':
      parts.push(text(t.secretAccepted));
      if (secret) parts.push(secret);
      if (entry.versionId) parts.push(text('·'), code(shortVersion(entry.versionId)!));
      break;
    case 'secret-ignored':
      parts.push(text(t.secretIgnored));
      if (secret) parts.push(secret);
      break;
    case 'tags-rewritten': {
      parts.push(text(t.tagsRewritten));
      const names = Array.isArray(details.secrets)
        ? details.secrets.filter((n): n is string => typeof n === 'string')
        : [];
      if (names.length) parts.push(text(t.tagsOn), code(names.join(', ')));
      break;
    }
    case 'secret-recovered':
      if (entry.outcome === 'pending') {
        parts.push(text(t.recoveryPending));
        if (secret) parts.push(secret);
        if (typeof details.waitedSeconds === 'number')
          parts.push(text(`(${t.stillRecovering(details.waitedSeconds)})`));
      } else {
        parts.push(text(t.secretRecovered));
        if (secret) parts.push(secret);
      }
      break;
    case 'references-refreshed':
      parts.push(text(t.referencesRefreshed));
      if (resource) parts.push(resource);
      if (str(details, 'trigger') === 'after-apply') parts.push(text(t.afterApply));
      break;
    case 'app-restarted': {
      // Feature spec 15: the platform's rollout (`action`); App Service restarts.
      const action = str(details, 'action');
      parts.push(
        text(
          action === 'roll-out' ? t.rolledOut : action === 'new-revision' ? t.newRevision : t.appRestarted,
        ),
      );
      if (resource) parts.push(resource);
      if (secret) parts.push(text(t.afterSetting), secret);
      break;
    }
    case 'secret-rotated':
      parts.push(text(t.secretRotated));
      if (secret) parts.push(secret);
      if (entry.versionId) parts.push(text('·'), code(shortVersion(entry.versionId)!));
      break;
    case 'vault-secrets-deleted': {
      const deleted = Array.isArray(details.deleted)
        ? details.deleted.filter((n): n is string => typeof n === 'string')
        : [];
      const kept = Array.isArray(details.kept)
        ? details.kept.filter((n): n is string => typeof n === 'string')
        : [];
      const until = str(details, 'recoverableUntil');
      parts.push(text(t.vaultSecretsDeleted(deleted.length)));
      if (deleted.length) parts.push(code(deleted.join(', ')));
      parts.push(text(until ? t.recoverableUntil(formatDay(until)) : t.permanently));
      if (kept.length) parts.push(text(t.kept(kept.join(', '))));
      break;
    }
    case 'vault-secrets-restored': {
      const restored = Array.isArray(details.restored)
        ? details.restored.filter((n): n is string => typeof n === 'string')
        : [];
      parts.push(text(t.vaultSecretsRestored(restored.length)));
      if (restored.length) parts.push(code(restored.join(', ')));
      break;
    }
    case 'secret-changed-outside':
      parts.push(text(t.changedOutside));
      if (secret) parts.push(secret);
      if (entry.versionId) parts.push(text('·'), code(shortVersion(entry.versionId)!));
      break;
    case 'secret-revealed': {
      // Two entries per reveal (spec 6.11): the request entry (pending) and the outcome entry naming it.
      const copy = str(details, 'purpose') === 'copy';
      const requestId = str(details, 'requestId');
      if (!requestId && entry.outcome === 'pending') {
        parts.push(text(t.revealRequested));
        if (secret) parts.push(secret);
        parts.push(text(t.purpose(copy ? 'copy' : 'view')), text(t.outcomeUnknown));
      } else if (entry.outcome === 'failed') {
        parts.push(text(copy ? t.copyFailed : t.revealFailed));
        if (secret) parts.push(secret);
        const errorCode = str(details, 'errorCode');
        if (errorCode) parts.push(code(errorCode));
      } else if (entry.outcome === 'canceled') {
        parts.push(text(t.revealCanceled));
        if (secret) parts.push(secret);
      } else {
        parts.push(text(copy ? t.copied : t.revealed));
        if (secret) parts.push(secret);
        if (entry.versionId) parts.push(text('·'), code(shortVersion(entry.versionId)!));
      }
      const reason = str(details, 'reason');
      if (reason) parts.push(text(t.reason(reason)));
      if (requestId) parts.push(text(`· ${t.entry}`), code(entryReference(requestId)));
      break;
    }
    case 'network-created':
      parts.push(text(na.created), text(networkName));
      break;
    case 'network-changed': {
      parts.push(text(na.changed));
      const lines = changeLines(details);
      changes = lines.slice(0, maxChanges);
      more = lines.length - changes.length;
      break;
    }
    case 'network-deleted':
      parts.push(text(na.deleted), text(networkName));
      break;
    case 'network-attached':
      parts.push(text(na.attached), deploymentPart(), text(na.to), text(networkName));
      break;
    case 'network-detached':
      parts.push(text(na.detached), deploymentPart(), text(na.from), text(networkName));
      break;
    case 'network-connected':
      parts.push(text(na.connected), deploymentPart(), text(na.to), text(networkName));
      break;
    case 'network-connect-failed': {
      parts.push(text(na.connectFailed), deploymentPart(), text(na.to), text(networkName));
      const errorCode = str(details, 'errorCode');
      if (errorCode) parts.push(code(errorCode));
      break;
    }
    case 'network-addresses-released':
      parts.push(text(na.released), deploymentPart('’s'), text(na.addressesIn), text(networkName));
      break;
    // Feature spec 17, phase 2: request instances (spec 11 6.15.26).
    case 'network-request-created':
    case 'network-request-approved':
    case 'network-request-rejected':
    case 'network-request-revoked':
    case 'network-request-restored': {
      const verb = entry.kind.replace('network-request-', '') as keyof typeof ra.verbs;
      parts.push(
        text(ra.verbs[verb]),
        code(str(details, 'requestId') ?? ''),
        text('('),
        deploymentPart(),
        text(')'),
      );
      const detail = str(details, 'detail');
      if (detail) parts.push(text(`· ${ra.detail(detail)}`));
      const reason = str(details, 'reason');
      if (reason && verb !== 'created') parts.push(text(`: ${reason}`));
      break;
    }
    case 'network-request-asked-again': {
      parts.push(
        text(ra.askedAgain),
        code(str(details, 'requestId') ?? ''),
        text('('),
        deploymentPart(),
        text(')'),
      );
      const reason = str(details, 'reason');
      if (reason) parts.push(text(`: ${reason}`));
      break;
    }
    case 'network-request-enforced':
    case 'network-request-enforcement-failed':
      parts.push(
        text(ra.decisionRun(entry.runId ? entry.runId.slice(-4) : '')),
        text(entry.kind.endsWith('failed') ? ra.failedToOpen : ra.opened),
        code(str(details, 'requestId') ?? ''),
        text('('),
        deploymentPart(),
        text(')'),
      );
      break;
    case 'network-request-stale':
    case 'network-request-withdrawn':
      parts.push(
        code(str(details, 'requestId') ?? ''),
        text('('),
        deploymentPart(),
        text(')'),
        text(entry.kind.endsWith('stale') ? ra.stale : ra.withdrawn),
      );
      break;
    // Feature spec 17, phase 3: origin secrets (never a value; spec 11 6.15.28).
    case 'network-origin-rotation-requested':
    case 'network-origin-switched':
    case 'network-origin-regenerated': {
      const oa = strings.networks.entryPoints.activity;
      const route = str(details, 'routeId') ?? '';
      const ep = str(details, 'entryPointId') ?? '';
      parts.push(
        text(
          entry.kind === 'network-origin-rotation-requested'
            ? oa.requested
            : entry.kind === 'network-origin-switched'
              ? oa.switched(networkName)
              : oa.regenerated(networkName),
        ),
        code(route),
        text(`(${ep})`),
      );
      if (entry.kind === 'network-origin-switched') parts.push(text(oa.toNew));
      break;
    }
    // Feature spec 17, phase 5: the typed commitment confirmation (AWS Shield Advanced).
    case 'network-commitment-confirmed': {
      const oa = strings.networks.entryPoints.activity;
      const terms =
        details.terms && typeof details.terms === 'object' && !Array.isArray(details.terms)
          ? (details.terms as JsonObject)
          : null;
      const monthly = typeof terms?.monthly === 'number' ? terms.monthly : null;
      const months = typeof terms?.months === 'number' ? terms.months : null;
      const currency = typeof terms?.currency === 'string' ? terms.currency : 'USD';
      const ids = Array.isArray(details.entryPointIds)
        ? details.entryPointIds.filter((x): x is string => typeof x === 'string')
        : [];
      parts.push(
        text(details.renewalOnly === true ? oa.confirmedRenewal : oa.confirmed),
        text(
          `${str(details, 'label') ?? str(details, 'addOn') ?? ''}${monthly !== null && months !== null ? oa.commitmentTerms(currency, monthly, months) : ''}`,
        ),
      );
      if (ids.length) parts.push(text(oa.forEntryPoints(ids.join(', '))));
      break;
    }
    default:
      parts.push(code(entry.kind));
      for (const target of [entry.secretName, entry.resourceId]) if (target) parts.push(code(target));
  }
  return { group: activityGroup(entry.kind), parts, changes, more, runId: entry.runId };
}

// ---------------------------------------------------------------- filters (spec 6.11.2)

/** The timeline's filter controls; `kind` is '', a kind, or `group:<group>`. */
export interface ActivityFilterState {
  kind: string;
  secret: string;
  resource: string;
  actor: string;
}

export const NO_ACTIVITY_FILTER: ActivityFilterState = { kind: '', secret: '', resource: '', actor: '' };

/** The query of the filter controls (empty members aren't sent). */
export function toActivityFilter(state: ActivityFilterState): ActivityFilter {
  const kind = state.kind.startsWith('group:')
    ? [...(ACTIVITY_GROUPS[state.kind.slice(6) as ActivityGroup] ?? [])]
    : state.kind
      ? [state.kind]
      : null;
  return {
    kind,
    secretName: state.secret.trim() || null,
    resourceId: state.resource || null,
    actor: state.actor.trim() || null,
  };
}

export function hasActivityFilter(state: ActivityFilterState): boolean {
  return !!(state.kind || state.secret.trim() || state.resource || state.actor.trim());
}

/** The filters kept in the deployment page's URL (`?kind=&secret=&resource=&actor=`). */
export function activityFilterFromParams(params: URLSearchParams): ActivityFilterState {
  return {
    kind: params.get('kind') ?? '',
    secret: params.get('secret') ?? '',
    resource: params.get('resource') ?? '',
    actor: params.get('actor') ?? '',
  };
}
